import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// Utils
import { Table, kaReducer } from 'ka-table';
import { SortDirection, SortingMode } from 'ka-table/enums';
import { isEmpty, isEqual, isNil } from 'lodash';
import { updateData, search } from 'ka-table/actionCreators';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

// Icons
import { ReactComponent as ChevronUp } from '../../../assets/icons/chevron_up.svg';
import { ReactComponent as ChevronDown } from '../../../assets/icons/chevron_down.svg';
import { ReactComponent as SearchIcon } from '../../../assets/icons/search.svg';
import { ReactComponent as ChevronLeft } from '../../../assets/icons/chevron_left.svg';
import { ReactComponent as ChevronRight } from '../../../assets/icons/chevron_right.svg';
import { ReactComponent as StockIcon } from '../../../assets/icons/stock.svg';

// Components
import InputField from '../../atoms/InputField/InputField';

import styles from './InventoryTable.module.scss';
import cn from '../../../utils/tools/cn';
import TableSkeleton from '../../views/establishment/Stocks/TableSkeleton';
import Checkbox from '../../atoms/Checkbox/Checkbox';
import SkeletonLoader from '../../molecules/SkeletonLoader/SkeletonLoader';

function InventoryTable({
  columns,
  rows,
  isSearchable,
  searchPlaceholder,
  filters,
  isLoading,
  handleColumnAction,
  inventories,
  isColumnActionLoading,
  onNavRight,
  onNavLeft,
  onMultipleEditionClick,
  multipleEditionLabel,
  isDisabledNavLeft,
  isDisableNavRight,
  tableClassName,
  isYieldsTable,
}) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const [rowsLength, setRowsLength] = useState(null);
  const [searchValue, setSearchValue] = useState('');

  const [tableProps, changeTableProps] = useState({
    columns,
    data: rows,
    rowKeyField: 'id',
    sortingMode: SortingMode.Single,
    search: ({ searchText, rowData }) => {
      if (rowData?.name?.props?.['data-search'] && searchText) {
        const value = rowData.name.props['data-search'];
        const searchTerms = searchText.split(' ');
        return searchTerms.every(
          (term) => (term.trim() ? value.toLowerCase().includes(term.toLowerCase()) : true),
        );
      }
      return false;
    },
  });

  const dispatch = (action) => {
    changeTableProps((prevState) => kaReducer(prevState, action));
  };

  useEffect(() => {
    dispatch(updateData(rows));

    if (!rowsLength && rows?.length) setRowsLength(rows.length);
  }, [rows]);

  useEffect(() => {
    if (!isEqual(columns, tableProps?.columns)) {
      changeTableProps((prevState) => ({ ...prevState, columns }));
    }
  }, [columns]);

  useEffect(() => {
    dispatch(search(searchValue));
  }, [searchValue]);

  const getCell = (rowData, field, column) => {
    if (column?.bold) return <strong className="fw-500 fs-14">{rowData[field]}</strong>;
    return rowData[field];
  };

  const getFilteredProductsIds = (dayId) => {
    const visibleProducts = searchValue
      ? rows?.filter((row) => tableProps.search({ searchText: searchValue, rowData: row }))
      : rows;
    const visibleProductIds = visibleProducts?.map((product) => product.id);

    const productIds = Object.keys(inventories)
      .filter((productId) => !isNil(inventories[productId]?.[dayId]?.id))
      .filter((productId) => visibleProductIds?.some((id) => id === productId));

    return productIds || [];
  };

  const isAllColumnCellsChecked = useCallback((dayId) => {
    if (!isEmpty(inventories) && dayId) {
      const itemsToCheck = getFilteredProductsIds(dayId).map((productId) => inventories[productId][dayId]?.isActive);
      return itemsToCheck?.length && itemsToCheck.every((value) => value === true);
    }

    return false;
  }, [inventories, searchValue]);

  const handleColumnActionClick = (dayId, checked) => {
    if (!isEmpty(inventories)) {
      handleColumnAction(
        dayId,
        checked,
        getFilteredProductsIds(dayId),
      );
    }
  };

  const getHeadCell = ({ actionOnColumn, title, dayId }) => (
    <label className="d-flex a-center c-pointer" htmlFor={dayId ? `dayAction_${dayId}` : undefined}>
      {actionOnColumn ? (
        <div className="m-r-10">
          <Checkbox
            id={`dayAction_${dayId}`}
            isChecked={isAllColumnCellsChecked(dayId)}
            onChange={(value) => handleColumnActionClick(dayId, value?.target?.checked)}
            disabled={isColumnActionLoading}
          />
        </div>
      ) : null}
      {title}
    </label>
  );

  const getSortIcon = (column) => {
    const up = <ChevronUp width={15} height={15} />;
    const down = <ChevronDown width={15} height={15} />;
    return column.sortDirection === SortDirection.Ascend ? up : down;
  };

  return (
    <div>
      {isSearchable ? (
        <div className="d-flex a-center mobile-a-start m-b-20 mobile-d-col">
          <div className={cn([filters ? 'input_max m-r-20 mobile-m-r-0 mobile-m-b-20' : ''])}>
            <InputField
              id="tableSearch"
              name="tableSearch"
              onChange={(e) => setSearchValue(e.currentTarget.value)}
              label={searchPlaceholder || t('common.search')}
              value={searchValue}
              icon={<SearchIcon />}
              className={styles.search}
              disabledBrowserSuggestions
            />
          </div>

          {filters ? <div className="input_max m-r-30 mobile-m-r-0 mobile-m-b-20">{filters}</div> : null}

          {multipleEditionLabel && onMultipleEditionClick ? (
            <button
              type="button"
              className="d-flex a-center c-primary-500 fw-600 c-pointer ws-nowrap"
              onClick={onMultipleEditionClick}
            >
              <StockIcon className="m-r-8" style={{ minWidth: 20 }} />
              {multipleEditionLabel}
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="d-flex a-center j-space-btw m-t-10 m-b-20">
        <div className="d-flex a-center m-l-auto">
          <button
            type="button"
            className={cn([styles.nav_arrow, 'd-flex center c-pointer m-r-10 c-primary-500'])}
            onClick={onNavLeft}
            disabled={isDisabledNavLeft}
          >
            <ChevronLeft />
          </button>
          <button
            type="button"
            className={cn([styles.nav_arrow, 'd-flex center c-pointer c-primary-500'])}
            onClick={onNavRight}
            disabled={isDisableNavRight}
          >
            <ChevronRight />
          </button>
        </div>
      </div>

      {isMobile && isYieldsTable ? (
        isLoading ? (
          <SkeletonLoader height={58} className="m-b-10" radius={5} />
        ) : (
          <div className={cn([styles.legend, 'm-b-10'])}>
            <p>
              <span className={cn([styles.tag, styles.low])} />
              {t('seasons.low')}
            </p>
            <p>
              <span className={cn([styles.tag, styles.medium])} />
              {t('seasons.medium')}
            </p>
            <p>
              <span className={cn([styles.tag, styles.high])} />
              {t('seasons.high')}
            </p>
          </div>
        )
      ) : null}

      <div className={cn([styles.wrapper, 'inventory_table', tableClassName])}>
        {isLoading ? (
          <TableSkeleton tableOnly rowsLength={rowsLength} />
        ) : (
          <Table
            {...tableProps}
            dispatch={dispatch}
            childComponents={{
              cellText: {
                content: ({ rowData, field, column }) => getCell(rowData, field, column),
                elementAttributes: ({ column }) => ({
                  'data-column': column.dataColumn || column.title,
                  'data-season': column.dataSeason || '',
                }),
              },
              sortIcon: {
                content: ({ column }) => getSortIcon(column),
              },
              headCell: {
                content: ({ column }) => {
                  if (column?.actionOnColumn) {
                    return getHeadCell(column);
                  }
                  return null;
                },
                elementAttributes: ({ column }) => {
                  if (column.sticky) {
                    return {
                      style: {
                        ...column.style,
                        position: 'sticky',
                        zIndex: 3,
                        left: 0,
                      },
                    };
                  }
                  return { style: column.style };
                },
              },
              cell: {
                elementAttributes: ({ column }) => {
                  if (column.sticky) {
                    return {
                      style: {
                        ...column.style,
                        position: 'sticky',
                        zIndex: 2,
                        left: 0,
                        backgroundColor: '#fff',
                      },
                    };
                  }
                  return { style: column.style };
                },
              },
            }}
          />
        )}
      </div>
    </div>
  );
}

InventoryTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  })),
  rows: PropTypes.arrayOf(PropTypes.shape({})),
  isSearchable: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  filters: PropTypes.node,
  isLoading: PropTypes.bool,
  handleColumnAction: PropTypes.func,
  inventories: PropTypes.shape({}),
  isColumnActionLoading: PropTypes.bool,
  onNavRight: PropTypes.func,
  onNavLeft: PropTypes.func,
  onMultipleEditionClick: PropTypes.func,
  multipleEditionLabel: PropTypes.string,
  isDisabledNavLeft: PropTypes.bool,
  isDisableNavRight: PropTypes.bool,
  tableClassName: PropTypes.string,
  isYieldsTable: PropTypes.bool,
};

InventoryTable.defaultProps = {
  columns: [],
  rows: [],
  isSearchable: false,
  searchPlaceholder: null,
  filters: null,
  isLoading: false,
  handleColumnAction: () => {},
  inventories: {},
  isColumnActionLoading: false,
  onNavRight: () => {},
  onNavLeft: () => {},
  onMultipleEditionClick: () => {},
  multipleEditionLabel: null,
  isDisabledNavLeft: false,
  isDisableNavRight: false,
  tableClassName: '',
  isYieldsTable: false,
};

export default InventoryTable;
